import OrderTag from "@/enum/OrderTag";
import { Form } from "antd";
import React from "react";
import { transformAliOutOrderTag2PlatformType } from "../utils";
import PrinterSelect from "@/components/PrinterSelect";
import OrderTagNameMap from "@/map/OrderTagNameMap";
import { getRequiredRule } from "@/utils/formValidatorRules";
import AliOutOrderTemplateSelectExtra from "./AliOutOrderTemplateSelectExtra";
import { observer } from "mobx-react-lite";
import WaybillTemplateSelect, {
    FilterableTemplateType,
    WaybillTemplateSelectProps,
} from "@/components/WaybillTemplateSelect2";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import MultiPlatformUseChildWaybillNoModeFormItem from "../MultiPlatformUseChildWaybillNoModeFormItem";
import { MultiShopSelectPlatformType } from "@/components/MultiShopSelect/types";
import { getWaybillTemplateTypeByOrderSourceCategory } from "@/components/WaybillTemplateSelect2/services";
import LogisticsCompany from "@/enum/LogisticsCompany";
import WaybillTemplateType from "@/enum/WaybillTemplateType";

interface AliOutOrderWaybillTemplateFormItemProps {
    tag: OrderTag;
    templateSelectProps: WaybillTemplateSelectProps;
}

const plaintextOutOrderTemplateFilterMap: Partial<Record<OrderTag, FilterableTemplateType>> = {
    [OrderTag.ALI_PLAINTEXT_OUT_ORDER_TAOBAO]: {
        key: OrderTag.ALI_PLAINTEXT_OUT_ORDER_TAOBAO,
        name: "菜鸟",
        waybillTemplateTypes: getWaybillTemplateTypeByOrderSourceCategory(
            MultiShopSelectPlatformType.ali_out_order_taobao,
        ),
    },
    [OrderTag.ALI_PLAINTEXT_OUT_ORDER_PDD]: {
        key: OrderTag.ALI_PLAINTEXT_OUT_ORDER_PDD,
        name: "拼多多",
        waybillTemplateTypes: getWaybillTemplateTypeByOrderSourceCategory(
            MultiShopSelectPlatformType.ali_out_order_pdd,
        ),
    },
    [OrderTag.ALI_PLAINTEXT_OUT_ORDER_DY]: {
        key: OrderTag.ALI_PLAINTEXT_OUT_ORDER_DY,
        name: "抖音",
        waybillTemplateTypes: getWaybillTemplateTypeByOrderSourceCategory(
            MultiShopSelectPlatformType.ali_out_order_dy,
        ),
    },
    [OrderTag.ALI_PLAINTEXT_OUT_ORDER_KWAI]: {
        key: OrderTag.ALI_PLAINTEXT_OUT_ORDER_KWAI,
        name: "快手",
        waybillTemplateTypes: getWaybillTemplateTypeByOrderSourceCategory(
            MultiShopSelectPlatformType.ali_out_order_kwai,
        ),
    },
    [OrderTag.ALI_PLAINTEXT_OUT_ORDER_XHS]: {
        key: OrderTag.ALI_PLAINTEXT_OUT_ORDER_XHS,
        name: "小红书",
        waybillTemplateTypes: getWaybillTemplateTypeByOrderSourceCategory(
            MultiShopSelectPlatformType.ali_out_order_xhs,
        ),
    },
    [OrderTag.ALI_PLAINTEXT_OUT_ORDER_JD]: {
        key: OrderTag.ALI_PLAINTEXT_OUT_ORDER_JD,
        name: "京东",
        waybillTemplateTypes: getWaybillTemplateTypeByOrderSourceCategory(
            MultiShopSelectPlatformType.ali_out_order_jd,
        ),
    },
};

const aliOutOrderJdAdditionalTemplateFilters = [
    {
        waybillTemplateType: WaybillTemplateType.OFFICIAL,
        logisticsCompanies: [LogisticsCompany.JDKY, LogisticsCompany.JD_EXPRESS, LogisticsCompany.JD_HPCP],
    },
];

const AliOutOrderWaybillTemplateFormItem: React.FC<AliOutOrderWaybillTemplateFormItemProps> = observer(function (
    props,
) {
    const { tag, templateSelectProps = {} } = props;
    const orderManagementStore = useOrderManagementStore();

    return (
        <Form.Item noStyle shouldUpdate>
            {(form) => {
                const values = form.getFieldsValue();
                const aliOutOrderPlatformType = transformAliOutOrderTag2PlatformType(tag);

                if (!aliOutOrderPlatformType) return null;

                const template = values[aliOutOrderPlatformType]?.template;
                const isGenerateWaybillNoOnly = values.isGenerateWaybillNoOnly;

                const printerSelectRules = !isGenerateWaybillNoOnly ? [getRequiredRule("打印机不能为空")] : null;

                return (
                    <Form.Item colon={false} label=" ">
                        <div style={{ display: "flex", width: "100%" }}>
                            <Form.Item
                                className="out-order-form-item"
                                name={[aliOutOrderPlatformType, "template"]}
                                label={`${OrderTagNameMap[tag]}模板`}
                                rules={[{ required: true }]}
                                extra={<AliOutOrderTemplateSelectExtra template={template} orderTag={tag} />}
                            >
                                <WaybillTemplateSelect
                                    shopId={orderManagementStore?.shopId}
                                    {...templateSelectProps}
                                    orderSourceCategory={aliOutOrderPlatformType}
                                    additionalTemplateFilters={
                                        tag === OrderTag.ALI_OUT_ORDER_JD
                                            ? aliOutOrderJdAdditionalTemplateFilters
                                            : undefined
                                    }
                                    filterableTemplateType={plaintextOutOrderTemplateFilterMap[tag]}
                                />
                            </Form.Item>

                            <Form.Item name={[aliOutOrderPlatformType, "printerName"]} rules={printerSelectRules}>
                                <PrinterSelect
                                    style={{ width: 200 }}
                                    className="ml10"
                                    disabled={isGenerateWaybillNoOnly}
                                    template={template}
                                />
                            </Form.Item>
                        </div>

                        <MultiPlatformUseChildWaybillNoModeFormItem platform={aliOutOrderPlatformType} />
                    </Form.Item>
                );
            }}
        </Form.Item>
    );
});

export default AliOutOrderWaybillTemplateFormItem;
